import { FormContext } from '../../../../utils/context/contextFactory';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { FormState } from '../../../../utils/state/initialStateFactory';
import { ErrorHandlers } from '../errorHandlers/errorHandlers';
import { isValidEmail } from '../../../../utils/form-validations';
import { AppliedDiscount } from '@wix/ambassador-totals-calculator/http';
import { FormStatus } from '../../../../types/form-state';
import { isFixedPrice } from '../../../../utils/payment/payment';

export type CalculatePaymentDetails = ({
  couponCode,
}: {
  couponCode?: string;
}) => Promise<void>;

export function createCalculatePaymentDetailsAction({
  actionFactoryParams,
  errorHandlers,
}: {
  actionFactoryParams: ActionFactoryParams<FormState, FormContext>;
  errorHandlers: ErrorHandlers;
}): CalculatePaymentDetails {
  return async ({ couponCode }) => {
    const [state, setState] = actionFactoryParams.getControllerState();
    const { formApi, reportError, experiments } = actionFactoryParams.context;
    const {
      formInputs: { numberOfParticipants, email },
      slotAvailability,
      service,
      selectedPaymentType,
      isBookingsOnEcom,
    } = state;
    const { id: serviceId, rate, payment } = service;
    const slot = slotAvailability?.slot!;

    setState({
      status: FormStatus.PROCESSING_PAYMENT_DETAILS,
    });
    const isBookingsFormInternalApiErrorHandlerEnabled = experiments.enabled(
      'specs.bookings.BookingsFormInternalApiErrorHandler',
    );

    const isFormDisplayPaymentdetailsWithDepositEnabled = experiments.enabled(
      'specs.bookings.EcomFormDisplayPaymentdetailsWithDeposit',
    );

    try {
      if (isBookingsOnEcom) {
        const {
          priceSummary,
          appliedDiscounts,
          payNow,
          payLater,
        } = await formApi.calculateTotalPrice({
          serviceId,
          price: service.payment.paymentDetails.price,
          deposit: isFormDisplayPaymentdetailsWithDepositEnabled
            ? state.paymentDetails.minCharge
            : undefined,
          numberOfParticipants,
          couponCode,
          email: isValidEmail(email) ? email : undefined,
          selectedPaymentType,
          onError: (error) => {
            errorHandlers.addError(error);
          },
        });

        if (isBookingsFormInternalApiErrorHandlerEnabled && !appliedDiscounts) {
          setState({ status: FormStatus.IDLE });
          return;
        }

        const [couponDiscount] = appliedDiscounts!;
        setState({
          couponInfo: {
            ...state.couponInfo,
            appliedCoupon: mapCouponDiscountToAppliedCoupon(couponDiscount),
          },
          paymentDetails: {
            ...state.paymentDetails,
            subtotalPerParticipant:
              Number(priceSummary?.subtotal?.amount) / numberOfParticipants,
            tax: Number(priceSummary?.tax?.amount),
            totalPrice: priceSummary?.total?.amount
              ? Number(priceSummary?.total?.amount)
              : state.paymentDetails.price,
            payNow: Number(payNow?.total?.amount),
            payLater:
              isFormDisplayPaymentdetailsWithDepositEnabled &&
              payLater?.total?.amount
                ? Number(payLater?.total?.amount)
                : state.paymentDetails.payLater,
          },
          status: FormStatus.IDLE,
        });
      } else {
        const paymentsDetails = await formApi.getPaymentsDetails({
          slot,
          numberOfParticipants,
          rate,
          serviceId,
          couponCode,
          email,
          isFixedPrice: isFixedPrice(payment),
          onError: (error) => {
            errorHandlers.addError(error);
          },
        });

        if (isBookingsFormInternalApiErrorHandlerEnabled && !paymentsDetails) {
          setState({ status: FormStatus.IDLE });
          return;
        }

        const appliedCoupon = paymentsDetails?.couponDetails;
        const finalPrice = paymentsDetails?.finalPrice;

        setState({
          couponInfo: {
            ...state.couponInfo,
            appliedCoupon,
          },
          paymentDetails: {
            ...state.paymentDetails,
            minCharge: finalPrice?.downPayAmount
              ? Number(finalPrice.downPayAmount)
              : state.paymentDetails.minCharge,
            totalPrice: finalPrice?.amount
              ? Number(finalPrice?.amount)
              : state.paymentDetails.price,
          },
          status: FormStatus.IDLE,
        });
      }
    } catch (error) {
      errorHandlers.addError(error);
      setState({ status: FormStatus.IDLE });
      reportError(error);
    }
  };
}

function mapCouponDiscountToAppliedCoupon(couponDiscount?: AppliedDiscount) {
  const coupon = couponDiscount?.coupon;
  return coupon
    ? {
        couponCode: coupon?.code,
        couponDiscount: coupon?.amount?.amount,
        couponId: coupon?.id,
        couponName: coupon?.name,
      }
    : undefined;
}
